import React, { forwardRef, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTheme } from '@mui/material/styles';
import { translate } from '../../locales';
import InputAdornment from '@mui/material/InputAdornment';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CircularProgress from '@mui/material/CircularProgress';
import MuiTextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '../Typography/Typography';
import CharCounter from './CharCounter';
import Tooltip from '../Tooltip/Tooltip';
import useIsTextOverflow from '../../customHooks/useIsTextOverflow';
import { useMultipleRefsSetter } from '../../customHooks/useMultipleRefsSetter';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/VisibilityOutlined';
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined';
import { getTextFieldSize } from './utils';
import { textFieldClasses as classes } from './textFieldClasses';
import {
  ClearIcon,
  CounterContainer,
  Footer,
  HelperMessage,
  HelpIcon,
  TextFieldRoot
} from './TextField.style';

const TextField = forwardRef(
  (
    {
      id,
      label = null,
      value,
      type,
      placeholder,
      error,
      isClearable = true,
      onChange = () => {},
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseLeave,
      loading,
      InputProps,
      multiline,
      fullWidth: incomingFullWidth,
      helperText,
      size: incomingSize,
      showCharCount = false,
      maxCharLength,
      TooltipProps,
      rootContainerElementProps = {},
      className: incomingClassName,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    const size = getTextFieldSize(incomingSize, multiline);
    const isFullWidth = incomingFullWidth || size === 'fullWidth';
    const [isTouched, setIsTouched] = useState(false);
    const [isHover, setIsHover] = useState(false);
    const [isTextOverflow, overFlowRef] = useIsTextOverflow(value);
    const inputRef = useRef();
    const [showPassword, setShowPassword] = useState(false);

    const callbackRef = useMultipleRefsSetter(ref, overFlowRef, inputRef);

    const isPasswordField = type === 'password';
    const readOnly = InputProps && InputProps.readOnly;
    const shouldRenderClearIcon =
      isTouched &&
      !multiline &&
      !readOnly &&
      isClearable &&
      !isPasswordField &&
      !!value;
    const counter = (value && value.length) || 0;
    const showFooter = helperText || showCharCount;

    const inputProps = {
      ...InputProps,
      endAdornment: (
        <>
          {shouldRenderClearIcon && (
            <InputAdornment
              data-qa-for={`${id}-text-field-clear-icon`}
              data-qa-id="text-field-clear-icon"
              position="end"
              onMouseDown={e => {
                // prevent focusing the button and losing focus from the input
                e.preventDefault();
                onChange({ target: { value: '' } });
              }}>
              <ClearIcon className={classes.clearIcon} fontSize="small" />
            </InputAdornment>
          )}
          {loading && (
            <InputAdornment
              position="end"
              data-qa-id="text-field-loader"
              data-qa-for={`${id}-text-field-loader`}>
              <CircularProgress size={20} data-qa-id="text-field-loader-icon" />
            </InputAdornment>
          )}
          {isPasswordField && (
            <InputAdornment position="end">
              <Tooltip
                title={
                  showPassword
                    ? translate('textField.password.hide')
                    : translate('textField.password.show')
                }>
                <IconButton
                  size="small"
                  aria-label="toggle password visibility"
                  data-qa-id="password-visibility-icon-container"
                  onClick={() => {
                    setShowPassword(v => !v);

                    // Set cursor at the end of the value
                    // We are using rAF so the browser would consider this before the paint of the new input element
                    // We can't use useEffect to prevent a cursor jump because useEffect runs too late
                    requestAnimationFrame(() => {
                      const input = inputRef.current;
                      const end = input.value.length;

                      input.setSelectionRange(end, end);
                    });
                  }}
                  onMouseDown={e => {
                    // prevent focusing the button and losing focus from the input
                    e.preventDefault();
                  }}>
                  {showPassword ? (
                    <Visibility data-qa-id="visibility-icon" fontSize="small" />
                  ) : (
                    <VisibilityOff
                      data-qa-id="visibilityoff-icon"
                      fontSize="small"
                    />
                  )}
                </IconButton>
              </Tooltip>
            </InputAdornment>
          )}
          {InputProps && InputProps.endAdornment}
        </>
      )
    };

    let fieldType = type;
    if (isPasswordField) {
      fieldType = showPassword ? 'text' : 'password';
      label = label ?? translate('textField.password.label');
      placeholder = placeholder ?? translate('textField.password.placeholder');
    }

    const containerProps = {
      ...rootContainerElementProps,
      className: clsx(
        classes.textFieldContainer,
        rootContainerElementProps.className,
        incomingClassName
      )
    };

    const showOverflowTextTooltip = isHover && !isTouched && isTextOverflow;

    return (
      <TextFieldRoot data-qa-id="text-field-root-container" {...containerProps}>
        {label && (
          <Grid container alignItems="center">
            <label
              htmlFor={id}
              className={classes.textFieldLabel}
              data-qa-id="text-field-label">
              <Typography variant="body2">{label}</Typography>
            </label>

            {TooltipProps && (
              <Tooltip
                placement="top"
                variant="info"
                data-qa-for={`${id}-text-field-tooltip`}
                data-qa-id="text-field-tooltip"
                {...TooltipProps}>
                <HelpIcon
                  className={classes.helpIcon}
                  color="action"
                  fontSize="small"
                />
              </Tooltip>
            )}
          </Grid>
        )}
        <Tooltip
          spacing={theme.spacing(0.75)}
          open={showOverflowTextTooltip}
          title={value || ''}>
          <MuiTextField
            data-qa-for={`${id}-text-field-root`}
            data-qa-id="text-field-root"
            // MuiTootlip is injecting dynamic aria-label to it's children and
            // in result we getting aria-label on MuiTextField's FormControl, which
            // causing a11y issues, so we override it with empty text.
            aria-label=""
            inputRef={callbackRef}
            id={id}
            value={value}
            type={fieldType}
            placeholder={placeholder}
            error={error}
            multiline={multiline}
            fullWidth={isFullWidth}
            size={size}
            onChange={onChange}
            onFocus={event => {
              if (onFocus) {
                onFocus(event);
              }
              setIsTouched(true);
            }}
            onBlur={event => {
              if (onBlur) {
                onBlur(event);
              }
              setIsTouched(false);
            }}
            onMouseEnter={event => {
              if (onMouseEnter) {
                onMouseEnter(event);
              }
              setIsHover(true);
            }}
            onMouseLeave={event => {
              if (onMouseLeave) {
                onMouseLeave(event);
              }
              setIsHover(false);
            }}
            inputProps={{ 'data-qa-id': 'text-field-input' }}
            InputProps={{
              ...inputProps
            }}
            {...rest}
          />
        </Tooltip>
        {showFooter && (
          <Footer
            container
            className={classes.footer}
            fullWidth={isFullWidth}
            size={size}
            showCharCount={showCharCount}>
            <Grid item>
              <Stack direction="row" spacing={0.75}>
                {error && (
                  <ErrorOutlineIcon
                    data-qa-for={`${id}-text-field-error-icon`}
                    data-qa-id="text-field-error-icon"
                    className={classes.errorIcon}
                    color="error"
                    sx={{ fontSize: 16 }}
                  />
                )}
                <HelperMessage
                  data-qa-for={`${id}-text-field-helper-text`}
                  variant="caption"
                  className={classes.helperMessage}
                  data-qa-id="text-field-helper-text"
                  error={error}>
                  {helperText}
                </HelperMessage>
              </Stack>
            </Grid>
            {showCharCount && (
              <CounterContainer item className={classes.counterContainer}>
                <CharCounter
                  id={id}
                  count={counter}
                  maxCharLength={maxCharLength}
                  className={classes.counter}
                />
              </CounterContainer>
            )}
          </Footer>
        )}
      </TextFieldRoot>
    );
  }
);

TextField.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  /** when passing a label id prop is required */
  label: PropTypes.string,
  isClearable: PropTypes.bool,
  error: PropTypes.bool,
  /** (event:object) => void */
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  loading: PropTypes.bool,
  multiline: PropTypes.bool,
  InputProps: PropTypes.object,
  /** defaults to 'm', (or 'xl' in case of multiline mode) */
  size: PropTypes.oneOf(['xs', 's', 'm', 'l', 'xl', 'fullWidth']),
  showCharCount: PropTypes.bool,
  maxCharLength: PropTypes.number,
  /**
   * Props that will get passed to [Tooltip](./index.html?path=/docs/components-feedback-tooltip--docs) component
   **/
  TooltipProps: PropTypes.object,
  /**
   * Props that will get passed to the root container HTML element
   */
  rootContainerElementProps: PropTypes.object
};

TextField.classes = classes;
TextField.shouldClassesExampleBeOnRoot = true;

TextField.displayName = 'TextField';
TextField.muiName = 'TextField';

export default TextField;
